// =============================================================================
// The product detail page's quantity control.
//
// Its own module because two consumers need it: the stepper buttons that write
// the field, and add-to-cart which reads it. Keeping the bounds in one place is
// what stops them disagreeing about what "1 to 20" means.
// =============================================================================

/** Smallest quantity the store will accept. */
const MIN_QTY = 1;

/** Ceiling used when a control carries no `data-qty-max`. */
const DEFAULT_MAX_QTY = 20;

/** The quantity field inside a scope, if that scope has one. */
function qtyField(scope) {
	return scope ? scope.querySelector("[data-qty]") : null;
}

/** The field's ceiling, read from the markup PHP rendered. */
function qtyMax(field) {
	const parsed = Number(field?.dataset.qtyMax);

	return Number.isFinite(parsed) && parsed >= MIN_QTY
		? Math.floor(parsed)
		: DEFAULT_MAX_QTY;
}

/**
 * The quantity to order, clamped.
 *
 * The field is free text — `inputmode="numeric"` only shapes the on-screen
 * keyboard — so whatever a visitor typed has to be read defensively rather than
 * trusted. Falls back to one when the scope has no control at all, which is the
 * case for every product card.
 *
 * @param {HTMLElement|null} scope Container to look for the field in.
 * @return {number} A whole number between 1 and the field's maximum.
 */
export function readQty(scope) {
	const field = qtyField(scope);

	if (!field) {
		return MIN_QTY;
	}

	const parsed = parseInt(field.value, 10);

	if (!Number.isFinite(parsed)) {
		return MIN_QTY;
	}

	return Math.min(Math.max(parsed, MIN_QTY), qtyMax(field));
}

/**
 * Write a quantity into the field, clamped to its bounds.
 *
 * @param {HTMLElement} field The `[data-qty]` input.
 * @param {number}      next  The value requested.
 */
export function writeQty(field, next) {
	field.value = String(Math.min(Math.max(next, MIN_QTY), qtyMax(field)));
}
